// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: MIT-0
/**
 * Web app configuration sourced from environment variables.
 *
 * After deploying the CDK stack, populate these values from the stack outputs:
 *   VITE_API_URL              → ApiGatewayUrl output
 *   VITE_COGNITO_USER_POOL_ID → UserPoolId output
 *   VITE_COGNITO_CLIENT_ID    → UserPoolClientId output
 *   VITE_LOCAL_AGENT_URL      → Local agent base URL (default http://127.0.0.1:8880)
 */

export interface AppConfig {
  /** API Gateway base URL (e.g. https://abc123.execute-api.us-east-1.amazonaws.com/prod) */
  apiUrl: string;
  /** Cognito User Pool ID (e.g. us-east-1_AbCdEfGhI) */
  cognitoUserPoolId: string;
  /** Cognito User Pool Client ID */
  cognitoClientId: string;
  /** Local agent base URL */
  localAgentUrl: string;
  /** Key algorithm for YubiKey PIV enrollment (ECC_P256 or RSA_2048) */
  keyAlgorithm: "ECC_P256" | "RSA_2048";
}

function env(key: string, fallback = ""): string {
  // Vite injects import.meta.env at build time
  const viteEnv = (import.meta as any).env;
  if (viteEnv?.[key]) {
    return viteEnv[key];
  }
  // Fallback for test environments (Jest uses process.env)
  if (typeof process !== "undefined" && process.env?.[key]) {
    return process.env[key]!;
  }
  return fallback;
}

export const appConfig: AppConfig = {
  apiUrl: env("VITE_API_URL"),
  cognitoUserPoolId: env("VITE_COGNITO_USER_POOL_ID"),
  cognitoClientId: env("VITE_COGNITO_CLIENT_ID"),
  localAgentUrl: env("VITE_LOCAL_AGENT_URL", "http://127.0.0.1:8880"),
  keyAlgorithm: (env("VITE_KEY_ALGORITHM", "ECC_P256") as "ECC_P256" | "RSA_2048"),
};
